/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { useMemo } from 'react';
import { CheckboxProps } from '../components/form/common';
import { SURVEY_TYPES } from '../components/reports-list';
import { CardGridViewMode } from '../components/survey-scope/CardGrid';
import { UtrvStatus } from '../constants/status';
import { ScopeGroups, SurveyActionData, SurveyGroupsData } from '../model/surveyData';
import {
  convertToQuestionList,
  createScopeQuestionGroup, createScopeQuestionGroupOptionalValue,
  initializeScopeGroupCustomMetrics,
  initializeScopeGroupFrameworks,
  initializeScopeGroupFrameworkSubGroups,
  initializeScopeGroupSDG,
  initializeScopeGroupStandards,
  scopeGroupProcessor,
  surveyGroupsByScopeGroupsProcessor,
  surveyGroupsByScopeSubGroupsProcessor,
  surveyGroupsBySDGAndScopeProcessor,
} from '../selectors/survey';
import { SurveyOverviewMode } from '../slice/surveySettings';
import { Materiality, MaterialityMap } from '../types/initiative';
import {
  BaseQuestionGroup,
  ScopeCategoryGroups,
  ScopeQuestionGroup,
  ScopeQuestionGroupOptionalValue,
  SurveySettings,
  SurveySummary,
  SurveyType
} from '../types/survey';
import { ScopeQuestion, ScopeQuestionOptionalValue } from '../types/surveyScope';
import { DataPeriods } from '../types/universalTracker';
import { getUtcDateUnit } from './date';
import { QUESTION } from '../constants/terminology';

export function getInitialSurveySettings(): SurveySettings {
  return {
    filterByStatus: [],
    filterByDelegationStatus: [],
    filterByMateriality: [],
    filterByGoal: [],
    filterByStandard: [],
    filterByFramework: [],
    filterByRegulatory: [],
    filterByRole: [],
    filterByUser: [],
    filterByTag: [],
    searchText: '',
    groupBy: ['sdg'],
    viewLayout: CardGridViewMode.gallery,
    filterByBookmarks: false,
  }
}

export const convertSurveyMaterialityToUtrCodeMap = (materialityMap?: MaterialityMap): MaterialityMap | undefined => {
  if (!materialityMap) {
    return;
  }
  return {
    [Materiality.Low]: materialityMap[Materiality.Low].map(c => `sdg/${c}`),
    [Materiality.Medium]: materialityMap[Materiality.Medium].map(c => `sdg/${c}`),
    [Materiality.High]: materialityMap[Materiality.High].map(c => `sdg/${c}`),
    [Materiality.None]: materialityMap[Materiality.None].map(c => `sdg/${c}`),
  }
}

export function getSurveyQuestionProgress(surveyGroups: ScopeQuestionGroup[]) {
  const progressStats = { completed: 0, count: 0, verified: 0, rejected: 0, updated: 0, created: 0 };
  if (!surveyGroups) {
    return progressStats
  }

  const uniqueIds = new Set();

  surveyGroups.forEach(questionGroup => {
    questionGroup.list.forEach(question => {
      // group contains duplicates, need to only count unique questions
      if (uniqueIds.has(question.utrv._id)) {
        return;
      }
      progressStats.count += 1;
      progressStats.completed += [UtrvStatus.Verified, UtrvStatus.Updated].includes(question.utrv.status as UtrvStatus) ? 1 : 0;
      progressStats.verified += [UtrvStatus.Verified].includes(question.utrv.status as UtrvStatus) ? 1 : 0;
      progressStats.rejected += question.utrv.status === UtrvStatus.Rejected ? 1 : 0;
      progressStats.updated += question.utrv.status === UtrvStatus.Updated ? 1 : 0;
      progressStats.created += question.utrv.status === UtrvStatus.Created ? 1 : 0;
      uniqueIds.add(question.utrv._id)
    });
  });
  return progressStats;
}

export function useUtrSurveyGroupsOptionalValue(
  overviewMode: SurveyOverviewMode,
  surveyData: SurveyGroupsData | undefined
): ScopeQuestionGroupOptionalValue[] {
  const { blueprintGroups, questionList } = useMemo(() => {

    if (!surveyData) {
      return { blueprintGroups: [], questionList: [] };
    }

    const scopeGroups = createScopeQuestionGroupOptionalValue(surveyData);
    const questionList = new Map<string, typeof scopeGroups[0]['list'][0]>();

    scopeGroups?.forEach(questionGroup => {
      questionGroup.list.forEach(question => {
        questionList.set(question.universalTracker.getId(), question);
      });
    });
    return { blueprintGroups: scopeGroups, questionList: Array.from(questionList.values()) };
  }, [surveyData]);

  if (!surveyData) {
    return [];
  }

  return getSurveyGroups(overviewMode, surveyData, blueprintGroups, questionList);
}

export const useSurveyGroups = (
  overviewMode: SurveyOverviewMode,
  surveyData?: SurveyActionData
): ScopeQuestionGroup[] => {
  const [
    blueprintGroups, questionList
  ] = useMemo(() => {
    if (!surveyData) {
      return [[], []];
    }
    const blueprintGroups = createScopeQuestionGroup(surveyData);
    const questionList = convertToQuestionList(blueprintGroups);
    return [blueprintGroups, questionList];
  }, [surveyData]);

  if (!surveyData) {
    return [];
  }

  return getSurveyGroups(overviewMode, surveyData, blueprintGroups, questionList);
}

export function getSurveyGroups<T extends ScopeQuestionOptionalValue = ScopeQuestion>(
  overviewMode: SurveyOverviewMode,
  surveyData: SurveyGroupsData<T>,
  blueprintGroups: BaseQuestionGroup<T>[],
  questionList: T[]
) {
  switch (overviewMode) {
    case SurveyOverviewMode.ScopeGroups:
      return getSurveyGroupsByScopeCategories(surveyData, questionList);
    case SurveyOverviewMode.Scope:
      return getSurveyGroupsByScopeSubCategories(surveyData, questionList);
    case SurveyOverviewMode.SdgAndScope:
      return getSurveyGroupsBySDGAndScope(surveyData, questionList);
    case SurveyOverviewMode.QuestionList:
      return [{ groupName: '', list: questionList }];
    case SurveyOverviewMode.Universal:
    default:
      return blueprintGroups;
  }
}

function initializeScopeGroups<T extends ScopeQuestionOptionalValue = ScopeQuestion>(surveyData: SurveyGroupsData): ScopeCategoryGroups<T> {
  return {
    [ScopeGroups.Custom]: initializeScopeGroupCustomMetrics(surveyData),
    [ScopeGroups.Sdg]: initializeScopeGroupSDG(),
    // Materiality scope is saved on surveyData as SDGs, so there is confusion when displaying scope groups
    // to figure out if the user originally selected SDGs or Materiality, which causes BOTH to be displayed.
    // As a dirty hack, we just will never show materiality as groups
    [ScopeGroups.Materiality]: {}, //initializeScopeGroupMateriality(),
    [ScopeGroups.Standards]: initializeScopeGroupStandards(),
    [ScopeGroups.Frameworks]: initializeScopeGroupFrameworks(),
  };
}

function initializeScopeSubCategories<T extends ScopeQuestionOptionalValue = ScopeQuestion>(surveyData: SurveyGroupsData) {
  return {
    [ScopeGroups.Custom]: initializeScopeGroupCustomMetrics<T>(surveyData),
    [ScopeGroups.Sdg]: initializeScopeGroupSDG<T>(),
    // Materiality scope is saved on surveyData as SDGs, so there is confusion when displaying scope groups
    // to figure out if the user originally selected SDGs or Materiality, which causes BOTH to be displayed.
    // As a dirty hack, we just will never show materiality as groups
    [ScopeGroups.Materiality]: {}, //initializeScopeGroupMateriality(),
    [ScopeGroups.Standards]: initializeScopeGroupStandards<T>(),
    [ScopeGroups.Frameworks]: initializeScopeGroupFrameworkSubGroups<T>(),
  };
}

function getScopeCategories<T extends ScopeQuestionOptionalValue = ScopeQuestion>(surveyData: SurveyGroupsData<T>, questionList: T[]) {
  const emptyScopeGroups = initializeScopeGroups<T>(surveyData);
  return scopeGroupProcessor(surveyData, questionList, emptyScopeGroups);
}

function getSurveyGroupsByScopeCategories<T extends ScopeQuestionOptionalValue = ScopeQuestion>(surveyData: SurveyGroupsData<T>, questionList: T[]) {
  return surveyGroupsByScopeGroupsProcessor<T>(
    surveyData,
    getScopeCategories(surveyData, questionList),
  );
}

function getScopeSubCategories<T extends ScopeQuestionOptionalValue = ScopeQuestion>(surveyData: SurveyGroupsData<T>, questionList: T[]) {
  const emptyScopeGroups = initializeScopeSubCategories<T>(surveyData);
  return scopeGroupProcessor(surveyData, questionList, emptyScopeGroups);
}

function getSurveyGroupsByScopeSubCategories<T extends ScopeQuestionOptionalValue = ScopeQuestion>(surveyData: SurveyGroupsData<T>, questionList: T[]) {
  return surveyGroupsByScopeSubGroupsProcessor(
    surveyData,
    getScopeSubCategories<T>(surveyData, questionList),
  );
}

function getSurveyGroupsBySDGAndScope<T extends ScopeQuestionOptionalValue = ScopeQuestion>(surveyData: SurveyGroupsData<T>, questionList: T[]) {
  return surveyGroupsBySDGAndScopeProcessor<T>(
    surveyData,
    getScopeCategories(surveyData, questionList),
  );
}

export const getCheckOptions: (canAccessVerification: boolean) => Pick<CheckboxProps, 'groupCode' | 'options'> = (
  canAccessVerification
) => ({
  groupCode: 'advancedOptions',
  options: [
    {
      code: 'verificationRequired',
      name: 'Verification required',
      instructions:
        'Select if you want to enforce that all answers/inputted data must be verified by someone in your organisation. This can provide additional credibility and enforces data governance.',
      disabled: !canAccessVerification,
    },
    {
      code: 'evidenceRequired',
      name: 'Evidence required',
      instructions:
        'Select if you want to enforce that every answer must include supporting evidence - such as a photo, video, file, or link to information. Supporting evidence is an important part of substantiating the data claim.',
    },
    {
      code: 'noteRequired',
      name: 'Further explanation/Notes required',
      instructions:
        `Select if you want to enforce that all answers/inputted data must also have text in the Further explanation section. Note: This will also apply to text ${QUESTION.PLURAL}, which could be unusual for users.`,
    },
    {
      code: 'isPrivate',
      name: 'Keep all private',
      instructions:
        `This will hide all answers and metrics from any investors, financial institutions, or regulators that you grant access to. We recommend leaving this off. You can mark individual ${QUESTION.PLURAL} as Private later if you wish to.`,
    },
  ],
});

export const hasSurveyConflict = (
  existingSurveys: SurveySummary[],
  props: {
    type: SurveyType;
    initiativeId?: string;
    year?: number;
    month?: number;
    sourceName?: string;
    period?: DataPeriods;
  },
  surveyId: string | undefined
) => {
  return existingSurveys.some((survey) => {
    return (
      surveyId !== survey._id &&
      survey.initiativeId === props.initiativeId &&
      (survey.type ?? SurveyType.Default) === props.type &&
      survey.sourceName === props.sourceName &&
      getUtcDateUnit('month', survey.effectiveDate) === props.month &&
      getUtcDateUnit('year', survey.effectiveDate) === props.year &&
      (survey.period ?? DataPeriods.Yearly) === props.period
    );
  });
};

export const getSurveyTypeOptions = () => [
  {
    label: SURVEY_TYPES.SURVEYS,
    value: SurveyType.Default,
  },
  { label: SURVEY_TYPES.COMBINE_SURVEYS, value: SurveyType.Aggregation },
];

export const isAggregatedSurvey = (surveyType: SurveyType | undefined) => {
  if (!surveyType) {
    return false;
  }
  return [SurveyType.Aggregation, SurveyType.AutoAggregation].includes(surveyType);
};

export const isAutoAggregatedSurvey = (surveyType: string | undefined) => {
  if (!surveyType) {
    return false;
  }
  return surveyType === SurveyType.AutoAggregation;
};
